import {
  fetchUserAttributes,
  getCurrentUser,
  signInWithRedirect,
} from '@aws-amplify/auth'
import { signIn } from '@aws-amplify/auth'
import { Row } from 'antd'
import { useFormik } from 'formik'
import { useCallback, useContext, useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { Button as SutraButton } from 'sutra/components/Button'
import { Form } from 'sutra/components/Form'
import { PopoverModal } from 'sutra/components/PopoverModal/PopoverModal'
import { accents, gray } from 'sutra/tokens/colors'
import { radii } from 'sutra/tokens/radii'
import * as Yup from 'yup'

import { handleException, runOperation } from 'clients/exception-handling'
import { localStorageClient } from 'clients/local-storage'
import { useRegisterBootstrapLoading } from 'components'
import { YogiInput } from 'components/ui/YogiInput'
import { AuthContext } from 'features/auth'
import { AuthCard } from 'features/auth/components/AuthCard/AuthCard'
import { AuthLayout } from 'features/auth/components/AuthLayout'
import { FormItem } from 'features/auth/components/FormItem'
import { Input, InputPassword } from 'features/auth/components/Input'
import { ServerValidationError } from 'features/auth/components/ServerValidationError'
import { isAuthErrorNamed } from 'features/auth/expected-auth-error'
import { RESET_PASSWORD, SETUP_PASSWORD } from 'features/auth/paths'
import { useAuthService } from 'features/auth/service'
import type { User } from 'features/auth/types/auth'
import { HOME } from 'features/projects/paths'
import { PROJECT_FILTERS_STORAGE_KEY } from 'features/projects/service/project-filters-storage'
import type { HashNavigationState } from 'routes/navigation-state'

const SignInSchema = Yup.object().shape({
  username: Yup.string().email('Invalid email').required('Required'),
  password: Yup.string().min(2, 'Too Short!').required('Required'),
})

// A first-time user signing in with a temporary password that already expired
// gets this specific Cognito refusal. It is the one sign-in failure that is
// safe to recover from automatically: Cognito only reports it when the
// temporary password itself matched, so the caller proved they hold it. A
// plain "Incorrect username or password" must never trigger the resend — it
// is indistinguishable from a guess and would let anyone flood an invitee's
// inbox with activation emails.
const isExpiredTemporaryPassword = (error: unknown): boolean => {
  const err = error as { name?: string; message?: string }
  return (
    err.name === 'NotAuthorizedException' &&
    /temporary password has expired/i.test(err.message ?? '')
  )
}

const ACTIVATION_RESENT_MESSAGE =
  'Your activation link expired. We sent you a new account activation email.'

// Informational counterpart to ServerValidationError: the resend outcome is
// an instruction to check email, not a failure, so it must not render in
// error styling.
const ActivationNotice = styled.div`
  padding: 8px 16px;

  background: ${accents.reflectionBlue[50]};
  border: 1px solid ${accents.reflectionBlue[200]};
  border-radius: 2px;
  color: ${gray[800]};
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
`

export const SignInForm = (): JSX.Element => {
  const authService = useAuthService()
  const federatedSignInOkta = async (): Promise<void> => {
    return signInWithRedirect({ provider: { custom: 'Okta' } })
  }

  const federatedSignInOktaMars = async (): Promise<void> => {
    return signInWithRedirect({ provider: { custom: 'OktaMARS' } })
  }

  const federatedSignInOktaSkullcandy = async (): Promise<void> => {
    return signInWithRedirect({ provider: { custom: 'OktaSkullcandy' } })
  }

  const federatedSignInAzure = async (): Promise<void> => {
    await signInWithRedirect({ provider: { custom: 'AzureActiveDirectory' } })
  }

  const federatedSignInAzureOpella = async (): Promise<void> => {
    await signInWithRedirect({ provider: { custom: 'AzureOpella' } })
  }

  const federatedSignInAzureLivingProof = async (): Promise<void> => {
    await signInWithRedirect({ provider: { custom: 'AzureLivingProof' } })
  }

  const federatedSignInOktaCPSkinHealth = async (): Promise<void> => {
    await signInWithRedirect({ provider: { custom: 'OktaCPSkinHealth' } })
  }

  const federatedSignInAzureClarins = async (): Promise<void> => {
    await signInWithRedirect({ provider: { custom: 'AzureClarins' } })
  }

  const federatedSignInOktaTyson = async (): Promise<void> => {
    await signInWithRedirect({ provider: { custom: 'OktaTyson' } })
  }

  const federatedSignInAzureDrBronners = async (): Promise<void> => {
    await signInWithRedirect({ provider: { custom: 'AzureDrBronners' } })
  }

  const federatedSignInAzureNUSA = async (): Promise<void> => {
    await signInWithRedirect({ provider: { custom: 'AzureNUSA' } })
  }

  const [oktaModalOpen, setOktaModalOpen] = useState(false)
  const [oktaEmail, setOktaEmail] = useState('')

  const [pingModalOpen, setPingModalOpen] = useState(false)
  const [pingEmail, setPingEmail] = useState('')
  const [showPingWarning, setShowPingWarning] = useState(false)

  const [azureModalOpen, setAzureModalOpen] = useState(false)
  const [azureEmail, setAzureEmail] = useState('')

  const checkOktaEmail = (email: string): void => {
    if (email.toLowerCase().match('.*@effem.com')) {
      federatedSignInOktaMars()
    } else if (email.toLowerCase().match('.*@skullcandy.com')) {
      federatedSignInOktaSkullcandy()
    } else if (
      email.toLowerCase().match('.*@cpskinhealth.com') ||
      email.toLowerCase().match('.*@eltamd.com') ||
      email.toLowerCase().match('.*@pcaskin.com')
    ) {
      federatedSignInOktaCPSkinHealth()
    } else if (email.toLowerCase().match('.*@tyson.com')) {
      federatedSignInOktaTyson()
    } else {
      federatedSignInOkta()
    }
  }

  const checkPingEmail = (email: string): void => {
    if (email.toLowerCase().match('.*@mdlz.com')) {
      setPingModalOpen(false)
      federatedSignInPing()
    } else {
      setShowPingWarning(true)
    }
  }

  const checkAzureEmail = (email: string): void => {
    if (
      email.toLowerCase().match('.*sanofi.com') ||
      email.toLowerCase().match('.*ssp.co.jp') ||
      email.toLowerCase().match('.*opella.com')
    ) {
      federatedSignInAzureOpella()
    } else if (email.toLowerCase().match('.*livingproof.com')) {
      federatedSignInAzureLivingProof()
    } else if (
      email.toLowerCase().match('.*clarins.com') ||
      email.toLowerCase().match('.*clarinsgroup.com')
    ) {
      federatedSignInAzureClarins()
    } else if (email.toLowerCase().match('.*drbronner.com')) {
      federatedSignInAzureDrBronners()
    } else if (email.toLowerCase().match('.*nestle.com')) {
      federatedSignInAzureNUSA()
    } else {
      federatedSignInAzure()
    }
    setAzureModalOpen(false)
  }

  const federatedSignInPing = async (): Promise<void> => {
    return signInWithRedirect({ provider: { custom: 'PingIdentity' } })
  }

  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  useRegisterBootstrapLoading('sign-in', loading)
  const [serverValidationError, setServerValidationError] = useState('')
  const [activationNotice, setActivationNotice] = useState('')
  const authContext = useContext(AuthContext)

  const handleRedirect = useCallback(() => {
    const searchParams = new URLSearchParams(location.search)
    if (searchParams.get('redirect')) {
      navigate(searchParams.get('redirect')!, {
        replace: true,
        state: { hashAuthority: 'url' } satisfies HashNavigationState,
      })
    } else {
      navigate(HOME)
    }
  }, [location.search, navigate])

  const formik = useFormik({
    initialValues: { username: '', password: '' },
    validationSchema: SignInSchema,
    onSubmit: async ({ username, password }) => {
      try {
        setLoading(true)

        localStorageClient.clearAllExcept([PROJECT_FILTERS_STORAGE_KEY])

        const { isSignedIn, nextStep } = await signIn({
          username: username.toLowerCase(),
          password,
        })

        if (username.indexOf('@meetyogi.com') !== -1) {
          void runOperation(() => window.ga?.('set', 'dimension1', 'yes'), {
            operation: 'auth.analytics.staff',
            recover: {
              reason: 'Sign-in continues without optional analytics.',
              run: () => undefined,
            },
          })
        }

        const user = await getCurrentUser()

        if (
          nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
        ) {
          authContext.setUser(user as User)
          navigate(SETUP_PASSWORD)
          return
        }

        if (isSignedIn) {
          setTimeout(() => {
            void runOperation(() => authService.updateLastVisit(), {
              operation: 'auth.last-visit.update',
            })
          }, 3000)

          const attributes = await fetchUserAttributes()

          await authContext.signIn(user as User)
          handleRedirect()
          toast.success(`Welcome back, ${attributes?.given_name}!`)
        }
      } catch (e) {
        handleException(e, {
          operation: 'auth.sign-in',
          disposition: 'expected',
          isExpectedError: (cause) =>
            isAuthErrorNamed(
              cause,
              'NotAuthorizedException',
              'UserNotFoundException',
              'UserNotConfirmedException',
              'PasswordResetRequiredException',
              'TooManyRequestsException',
            ),
          reason: 'Identity provider rejected the supplied credentials.',
        })

        if (!isExpiredTemporaryPassword(e)) {
          setLoading(false)
          setServerValidationError((e as Error).message)
          return
        }
        // Keep `loading` on until the resend settles so a second submit
        // cannot fire a second email while the first is in flight.
        try {
          await authService.resendAccountActivation(username.toLowerCase())
          setActivationNotice(ACTIVATION_RESENT_MESSAGE)
        } catch (resendError) {
          handleException(resendError, {
            operation: 'auth.activation.resend',
            disposition: 'failed',
          })

          setServerValidationError((e as Error).message)
        }
      } finally {
        setLoading(false)
      }
    },
  })

  if (window.$crisp) {
    window.$crisp.push(['do', 'chat:show'])
    window.$crisp.push([
      'on',
      'chat:closed',
      () => {
        window.$crisp?.push(['do', 'chat:show'])
      },
    ])
  }

  // Might need this useEffect to catch
  useEffect(() => {
    if (authContext.isAuthenticated) {
      handleRedirect()
    }
  }, [authContext.isAuthenticated, handleRedirect])

  const usernameError = formik.touched.username && formik.errors.username
  const passwordError = formik.touched.password && formik.errors.password

  const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setServerValidationError('')
    setActivationNotice('')
    formik.handleChange(e)
  }

  return (
    <>
      <AuthLayout showSignUp>
        <AuthCard>
          <StyledForm onFinish={formik.submitForm} layout="vertical">
            <StyledFormItem
              label="Email"
              validateStatus={usernameError ? 'error' : undefined}
              help={formik.touched.username && formik.errors.username}
            >
              <Input
                name="username"
                error={!!usernameError}
                autoComplete="username"
                placeholder="john.smith@acmelabs.com"
                onChange={onChange}
                value={formik.values.username}
              />
            </StyledFormItem>
            <StyledFormItem
              label="Password"
              validateStatus={passwordError ? 'error' : undefined}
              help={passwordError}
            >
              <InputPassword
                name="password"
                error={!!passwordError}
                placeholder="********"
                autoComplete="current-password"
                onChange={onChange}
                value={formik.values.password}
              />
            </StyledFormItem>

            <RowHelpers>
              <LinkForgot to={RESET_PASSWORD}>Forgot password?</LinkForgot>
            </RowHelpers>
            {serverValidationError && (
              <ServerValidationError message={serverValidationError} />
            )}
            {activationNotice && (
              <ActivationNotice role="status">
                {activationNotice}
              </ActivationNotice>
            )}
            <SignInButton type="submit" disabled={loading}>
              Sign in
            </SignInButton>
            <Divider style={{ marginTop: '18px' }}>
              Or continue with SSO
            </Divider>
            <SSORow>
              <SutraButton
                $variant="secondary"
                $size="sm"
                $height="44px"
                style={{ width: '100%', fontWeight: 400 }}
                onClick={() => setAzureModalOpen(true)}
              >
                Azure
              </SutraButton>
              <SutraButton
                $variant="secondary"
                $size="sm"
                $height="44px"
                style={{ width: '100%', fontWeight: 400 }}
                onClick={() => setOktaModalOpen(true)}
              >
                Okta
              </SutraButton>
              <SutraButton
                $variant="secondary"
                $size="sm"
                $height="44px"
                style={{ width: '100%', fontWeight: 400 }}
                onClick={() => setPingModalOpen(true)}
              >
                PingIdentity
              </SutraButton>
            </SSORow>
          </StyledForm>
        </AuthCard>
      </AuthLayout>
      <PopoverModal
        title={'Enter Okta email'}
        isOpen={oktaModalOpen}
        footer={
          <SutraButton
            variant="primary"
            onClick={() => checkOktaEmail(oktaEmail)}
          >
            Submit
          </SutraButton>
        }
        onClose={() => setOktaModalOpen(false)}
        width={520}
        content={
          <div>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                checkOktaEmail(oktaEmail)
              }}
            >
              <div style={{ display: 'flex', gap: 10 }}>
                <YogiInput
                  placeholder={'Email address'}
                  value={oktaEmail}
                  onChange={(e) => setOktaEmail(e.target.value)}
                />
              </div>
            </form>
          </div>
        }
      />
      <PopoverModal
        title={'Enter email for PingIdentity'}
        isOpen={pingModalOpen}
        footer={
          <SutraButton
            variant="primary"
            onClick={() => checkPingEmail(pingEmail)}
          >
            Submit
          </SutraButton>
        }
        onClose={() => setPingModalOpen(false)}
        width={520}
        content={
          <div>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                checkPingEmail(pingEmail)
              }}
            >
              <div style={{ display: 'flex', gap: 10 }}>
                <YogiInput
                  placeholder={'Email address'}
                  value={pingEmail}
                  onChange={(e) => setPingEmail(e.target.value)}
                />
              </div>
            </form>
            {showPingWarning && (
              <p style={{ color: 'red', marginTop: 10, marginBottom: 0 }}>
                Your organization does not support this SSO option with Yogi at
                this time.
              </p>
            )}
          </div>
        }
      />
      <PopoverModal
        title={'Enter email for Azure SSO'}
        isOpen={azureModalOpen}
        footer={
          <SutraButton
            variant="primary"
            onClick={() => checkAzureEmail(azureEmail)}
          >
            Submit
          </SutraButton>
        }
        onClose={() => setAzureModalOpen(false)}
        width={520}
        content={
          <div>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                checkAzureEmail(azureEmail)
              }}
            >
              <div style={{ display: 'flex', gap: 10 }}>
                <YogiInput
                  placeholder={'Email address'}
                  value={azureEmail}
                  onChange={(e) => setAzureEmail(e.target.value)}
                />
              </div>
            </form>
          </div>
        }
      />
    </>
  )
}

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`

const RowHelpers = styled(Row)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  font-size: 14px;
`

const LinkForgot = styled(Link)`
  color: ${accents.reflectionBlue[500]};
  font-size: 13px;

  :hover {
    text-decoration: underline;
  }
`

const SignInButton = styled(SutraButton)`
  width: 100%;
  height: 48px;
  font-weight: 600;
  border-radius: ${radii.sm}px;
`

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${gray[500]};
  font-family: 'Poppins', system-ui, sans-serif;
  font-weight: 500;
  font-size: 14px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${gray[300]};
  }
`

const SSORow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
`

const StyledFormItem = styled(FormItem)`
  margin: 0;
  width: 100%;
`
